<script lang="ts">
  import { STORE } from "../../state.svelte";
  import { shortAddress, timeAgo } from "../../utils";
  import PoolBadge from "../elements/PoolBadge.svelte";
  import CandleChart from "./CandleChart.svelte";
  import type { Candle } from "../utils/indicators";
  import type { Trade } from "sailfish-sdk";
  import { onDestroy } from "svelte";

  let { poolAddress = "", onBack = () => {} }: { poolAddress: string; onBack: () => void } = $props();

  let inputAddress = $state(poolAddress);
  let activeAddress = $state(poolAddress);

  const MAX_CANDLES = 120;
  const MAX_TRADES = 100;
  const CANDLE_INTERVAL = 1000;

  // Reactive state for rendering (assigned once per tick)
  let candles: Candle[] = $state([]);
  let poolTrades: Trade[] = $state([]);

  // Plain (non-reactive) buffers for accumulation
  let _candles: Candle[] = [];
  let _poolTrades: Trade[] = [];
  let _seenTxHashes = new Set<string>();

  // Pool info
  let poolInfo = $derived(
    activeAddress ? STORE.bot.sailfish.getPoolInfo(activeAddress) : null
  );

  let pairName = $derived(
    poolInfo
      ? `${poolInfo.base_token.symbol} / ${poolInfo.quote_token.symbol}`
      : activeAddress ? shortAddress(activeAddress, 6, 6) : "Select a pool"
  );

  // Process new trades into plain buffers (called by interval, NOT by $effect)
  function processNewTrades() {
    if (!activeAddress) return;

    const allTrades = STORE.bot.trades;

    const newForPool: Trade[] = [];
    for (const t of allTrades) {
      if (t.pool_address !== activeAddress) continue;
      if (_seenTxHashes.has(t.index.tx_hash)) break;
      newForPool.push(t);
    }

    if (newForPool.length === 0) return;

    for (const t of newForPool) {
      _seenTxHashes.add(t.index.tx_hash);
    }
    if (_seenTxHashes.size > 1000) {
      const entries = Array.from(_seenTxHashes);
      _seenTxHashes = new Set(entries.slice(entries.length - 500));
    }

    // Bucket trades into plain candle objects (oldest first)
    for (const trade of newForPool.reverse()) {
      const price = parseFloat(trade.price) || 0;
      const volume = Math.abs(parseFloat(trade.quote_amount) || 0);
      if (price === 0) continue;

      const now = Date.now();
      const bucketTime = Math.floor(now / CANDLE_INTERVAL) * CANDLE_INTERVAL;

      if (_candles.length > 0 && _candles[_candles.length - 1].timestamp === bucketTime) {
        const c = _candles[_candles.length - 1];
        c.high = Math.max(c.high, price);
        c.low = Math.min(c.low, price);
        c.close = price;
        c.volume += volume;
        c.trades += 1;
      } else {
        _candles.push({
          timestamp: bucketTime,
          open: price, high: price, low: price, close: price,
          volume, trades: 1,
        });
        if (_candles.length > MAX_CANDLES) _candles.shift();
      }
    }

    _poolTrades = [...newForPool, ..._poolTrades].slice(0, MAX_TRADES);

    // Single reactive assignment — triggers ONE update cycle
    candles = [..._candles];
    poolTrades = [..._poolTrades];
  }

  // 1 Hz update — matches candle interval and sibling component patterns
  const iv = setInterval(processNewTrades, 1000);
  onDestroy(() => clearInterval(iv));

  const selectPool = () => {
    if (inputAddress.trim()) {
      activeAddress = inputAddress.trim();
      // Reset both reactive state and plain buffers
      _candles = [];
      _poolTrades = [];
      _seenTxHashes = new Set();
      candles = [];
      poolTrades = [];
      if (STORE.bot.sailfish.getPoolInfo(activeAddress) === null) {
        STORE.bot.sailfish.fetchPoolInfo(activeAddress);
      }
    }
  };

  const formatPrice = (p: number) => {
    if (p >= 1) return p.toFixed(4);
    if (p >= 0.001) return p.toFixed(6);
    return p.toFixed(9);
  };

  const formatVol = (v: number) => {
    if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
    return v.toFixed(2);
  };

  const liveTimeFormat = (ts: number) => {
    const t = new Date(ts);
    return `${t.getMinutes()}:${String(t.getSeconds()).padStart(2, "0")}`;
  };

  let lastPrice = $derived(candles.length > 0 ? candles[candles.length - 1].close : 0);
</script>

<div class="px-4">
  <!-- Pool Selector -->
  <div class="flex items-center gap-3 mb-6">
    <input
      type="text"
      class="input bg-black border-gray-800 text-white font-mono text-sm flex-1 rounded-sm"
      placeholder="Enter pool address..."
      value={inputAddress}
      oninput={(e) => inputAddress = (e.target as HTMLInputElement).value}
      onkeydown={(e) => { if (e.key === "Enter") selectPool(); }}
    />
    <button
      class="btn bg-sky-600 hover:bg-sky-500 text-white border-none rounded-sm uppercase tracking-widest font-bold text-xs transition-colors"
      onclick={selectPool}
    >
      Load
    </button>
  </div>

  <!-- Pool Header -->
  {#if activeAddress}
    <div class="flex flex-wrap items-center gap-4 mb-6">
      {#if poolInfo}
        <PoolBadge poolType={poolInfo.pool_type} />
      {/if}
      <div>
        <h2 class="text-xl font-bold text-white">{pairName}</h2>
        <a
          href={`https://dexscreener.com/solana/${activeAddress}`}
          target="_blank"
          class="text-xs text-gray-600 hover:text-sky-500 font-mono transition-colors"
        >
          {shortAddress(activeAddress, 8, 8)}
        </a>
      </div>
      {#if lastPrice > 0}
        <div class="ml-auto text-right">
          <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest">Price</p>
          <p class="text-2xl font-black text-white font-mono tabular-nums">{formatPrice(lastPrice)}</p>
        </div>
      {/if}
    </div>

    <!-- Candlestick Chart -->
    <div class="mb-6">
      <CandleChart
        {candles}
        timeFormat={liveTimeFormat}
        placeholder="Waiting for trades to build candles..."
      />
    </div>

    <!-- Stats Row -->
    {#if candles.length > 0}
      {@const totalVol = candles.reduce((a, c) => a + c.volume, 0)}
      {@const totalTx = candles.reduce((a, c) => a + c.trades, 0)}
      <div class="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
        <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-3 text-center">
          <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Candles</p>
          <p class="text-lg font-black text-white tabular-nums">{candles.length}</p>
        </div>
        <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-3 text-center">
          <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Volume</p>
          <p class="text-lg font-black text-white tabular-nums">{formatVol(totalVol)}</p>
        </div>
        <div class="bg-gray-950/40 border border-gray-800/60 rounded-sm p-3 text-center">
          <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-1">Trades</p>
          <p class="text-lg font-black text-white tabular-nums">{totalTx}</p>
        </div>
      </div>
    {/if}

    <!-- Trade Table -->
    <div>
      <p class="text-[10px] font-mono text-gray-600 uppercase tracking-widest mb-3">Recent Trades <span class="text-gray-700">({poolTrades.length})</span></p>
      <div class="overflow-x-auto">
        <table class="w-full text-xs font-mono">
          <thead>
            <tr class="text-gray-600 text-left border-b border-gray-800">
              <th class="py-1.5 px-2 font-normal">Age</th>
              <th class="py-1.5 px-2 font-normal text-right">Price</th>
              <th class="py-1.5 px-2 font-normal text-right">Quote</th>
              <th class="py-1.5 px-2 font-normal text-right hidden sm:table-cell">Base</th>
              <th class="py-1.5 px-2 font-normal text-right hidden md:table-cell">Wallet</th>
              <th class="py-1.5 px-2 font-normal text-right">Tx</th>
            </tr>
          </thead>
          <tbody>
            {#each poolTrades as trade (trade.index.tx_hash)}
              <tr class="border-b border-gray-800/50 hover:bg-gray-900/50">
                <td class="py-1.5 px-2 text-gray-500 tabular-nums">{timeAgo((trade.index as any).tick_time)}</td>
                <td class="py-1.5 px-2 text-right text-white tabular-nums">{formatPrice(parseFloat(trade.price) || 0)}</td>
                <td class="py-1.5 px-2 text-right text-gray-400 tabular-nums">{(Math.abs(parseFloat(trade.quote_amount) || 0)).toFixed(4)}</td>
                <td class="py-1.5 px-2 text-right text-gray-400 tabular-nums hidden sm:table-cell">{(Math.abs(parseFloat(trade.base_amount) || 0)).toFixed(4)}</td>
                <td class="py-1.5 px-2 text-right hidden md:table-cell">
                  <a href={`https://solscan.io/account/${trade.from_wallet}`} target="_blank" class="text-gray-500 hover:text-white transition-colors">{shortAddress(trade.from_wallet, 4, 3)}</a>
                </td>
                <td class="py-1.5 px-2 text-right">
                  <a href={`https://solscan.io/tx/${trade.index.tx_hash}`} target="_blank" class="text-sky-500/70 hover:text-sky-500 transition-colors">View</a>
                </td>
              </tr>
            {/each}
            {#if poolTrades.length === 0}
              <tr><td colspan="6" class="py-8 text-center text-gray-600 italic">Waiting for trades on this pool...</td></tr>
            {/if}
          </tbody>
        </table>
      </div>
    </div>
  {:else}
    <div class="text-center py-20">
      <p class="text-gray-600">Enter a pool address above or click a pool from the Top Pools tab.</p>
    </div>
  {/if}
</div>
